import React, { useState, useEffect, useMemo } from 'react';
import { ZodiacSign } from '../types';
import { getLoveCompatibility, CompatibilityResult } from '../services/geminiService';
import { getPreloadedLoveCompatibility } from '../data/loveCompatibilityPreloaded';
import { HeartIcon, SparklesIcon, UserIcon, MessageCircleIcon, FlameIcon, AlertCircleIcon, RefreshCwIcon, PhoneIcon, ZodiacIcon } from './Icons';
import { useClient } from '../contexts/ClientContext';

// Données spécifiques pour l'affichage (Symboles Unicode + Dates + Éléments)
const ZODIAC_DATA: Record<string, { dates: string; element: string; color: string }> = {
    [ZodiacSign.Aries]: { dates: "21 Mar - 19 Avr", element: "Feu", color: "from-rose-500 to-red-600" },
    [ZodiacSign.Taurus]: { dates: "20 Avr - 20 Mai", element: "Terre", color: "from-emerald-500 to-teal-600" },
    [ZodiacSign.Gemini]: { dates: "21 Mai - 20 Juin", element: "Air", color: "from-amber-400 to-orange-500" },
    [ZodiacSign.Cancer]: { dates: "21 Juin - 22 Juil", element: "Eau", color: "from-cyan-500 to-blue-600" },
    [ZodiacSign.Leo]: { dates: "23 Juil - 22 Août", element: "Feu", color: "from-amber-500 to-yellow-600" },
    [ZodiacSign.Virgo]: { dates: "23 Août - 22 Sep", element: "Terre", color: "from-teal-500 to-emerald-600" },
    [ZodiacSign.Libra]: { dates: "23 Sep - 22 Oct", element: "Air", color: "from-pink-400 to-rose-500" },
    [ZodiacSign.Scorpio]: { dates: "23 Oct - 21 Nov", element: "Eau", color: "from-purple-600 to-indigo-700" },
    [ZodiacSign.Sagittarius]: { dates: "22 Nov - 21 Déc", element: "Feu", color: "from-violet-500 to-purple-600" },
    [ZodiacSign.Capricorn]: { dates: "22 Déc - 19 Jan", element: "Terre", color: "from-stone-500 to-neutral-600" },
    [ZodiacSign.Aquarius]: { dates: "20 Jan - 18 Fév", element: "Air", color: "from-sky-400 to-indigo-500" },
    [ZodiacSign.Pisces]: { dates: "19 Fév - 20 Mar", element: "Eau", color: "from-indigo-400 to-cyan-500" },
};

// Particules de cœur romantiques flottantes
const FLOATING_HEARTS = [
    { type: "heart", size: "w-6 h-6", left: "6%", delay: "0s", duration: "12s" },
    { type: "sparkle", size: "w-5 h-5", left: "14%", delay: "2s", duration: "14s" },
    { type: "heart", size: "w-7 h-7", left: "22%", delay: "4s", duration: "10s" },
    { type: "heart", size: "w-8 h-8", left: "30%", delay: "1s", duration: "13s" },
    { type: "sparkle", size: "w-4 h-4", left: "42%", delay: "3s", duration: "15s" },
    { type: "heart", size: "w-6 h-6", left: "55%", delay: "0.5s", duration: "11s" },
    { type: "sparkle", size: "w-5 h-5", left: "68%", delay: "2.5s", duration: "13s" },
    { type: "heart", size: "w-7 h-7", left: "78%", delay: "1.5s", duration: "12s" },
    { type: "sparkle", size: "w-4 h-4", left: "88%", delay: "3.5s", duration: "14s" },
    { type: "heart", size: "w-8 h-8", left: "94%", delay: "2s", duration: "11s" },
];

export const LoveCompatibility: React.FC = () => {
    const { client, recordDivinationActivity } = useClient();
    const [sign1, setSign1] = useState<string | null>(null);
    const [sign2, setSign2] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<CompatibilityResult | null>(null);
    const [displayScore, setDisplayScore] = useState(0);

    // Animation du score
    useEffect(() => {
        if (result && !loading) {
            let start = 0;
            const end = result.score;
            const duration = 1600;
            const increment = end / (duration / 16);

            const timer = setInterval(() => {
                start += increment;
                if (start >= end) {
                    setDisplayScore(end);
                    clearInterval(timer);
                } else {
                    setDisplayScore(Math.floor(start));
                }
            }, 16);
            return () => clearInterval(timer);
        }
    }, [result, loading]);

    const handleCalculate = async () => {
        if (!sign1 || !sign2) return;
        setLoading(true);
        setResult(null);
        setDisplayScore(0);

        try {
            const minTime = new Promise(resolve => setTimeout(resolve, 2200));
            const apiCall = getLoveCompatibility(sign1, sign2);
            const [_, data] = await Promise.all([minTime, apiCall]);

            let finalRes: CompatibilityResult;
            if (data && typeof data.score === 'number' && data.emotional && data.communication && data.intimacy && Array.isArray(data.challenges) && data.advice) {
                finalRes = data;
            } else {
                finalRes = getPreloadedLoveCompatibility(sign1, sign2);
            }
            setResult(finalRes);

            if (client) {
                recordDivinationActivity(
                    'love-match',
                    'Compatibilité Amoureuse',
                    `${sign1} & ${sign2} : Score ${finalRes.score}%`,
                    { sign1, sign2, result: finalRes }
                );
            }
        } catch (e) {
            const fallback = getPreloadedLoveCompatibility(sign1, sign2);
            setResult(fallback);
            if (client) {
                recordDivinationActivity(
                    'love-match',
                    'Compatibilité Amoureuse',
                    `${sign1} & ${sign2} : Score ${fallback.score}%`,
                    { sign1, sign2, result: fallback }
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setResult(null);
        setSign1(null);
        setSign2(null);
        setDisplayScore(0);
    };

    const loveTag = useMemo(() => {
        if (!result) return "";
        if (result.score >= 90) return "Âmes Sœurs Destinées";
        if (result.score >= 80) return "Passion & Harmonie Magique";
        if (result.score >= 70) return "Forte Alchimie Céleste";
        if (result.score >= 50) return "Attraction Magnétique & Défis";
        return "Complémentarité Karmique Intrigante";
    }, [result]);

    return (
        <section className="py-12 sm:py-16 bg-transparent min-h-screen relative overflow-hidden flex items-center justify-center">
            {/* --- STYLES CSS PERSONNALISÉS POUR L'ANIMATION DE L'AMOUR --- */}
            <style>{`
                @keyframes heartbeat {
                    0% { transform: scale(1); }
                    14% { transform: scale(1.18); }
                    28% { transform: scale(1); }
                    42% { transform: scale(1.28); }
                    70% { transform: scale(1); }
                }
                .animate-heartbeat {
                    animation: heartbeat 1.4s ease-in-out infinite;
                }
                @keyframes floatUpLove {
                    0% {
                        transform: translateY(0) translateX(0) scale(0.8) rotate(0deg);
                        opacity: 0;
                    }
                    15% {
                        opacity: 0.65;
                    }
                    85% {
                        opacity: 0.7;
                    }
                    100% {
                        transform: translateY(-80vh) translateX(35px) scale(1.2) rotate(20deg);
                        opacity: 0;
                    }
                }
                .animate-float-heart {
                    animation: floatUpLove 12s linear infinite;
                }
                @keyframes loveHaloPulse {
                    0%, 100% {
                        opacity: 0.45;
                        transform: scale(1);
                    }
                    50% {
                        opacity: 0.85;
                        transform: scale(1.08);
                    }
                }
                .animate-love-halo {
                    animation: loveHaloPulse 4s ease-in-out infinite;
                }
                @keyframes loveShineSweep {
                    0% { transform: translateX(-150%) rotate(25deg); }
                    100% { transform: translateX(250%) rotate(25deg); }
                }
                .animate-love-shine {
                    animation: loveShineSweep 5s ease-in-out infinite;
                }
            `}</style>
            
            {/* --- FOND ENVELOPPANT D'AMOUR (AURORAS & GLOW) --- */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
                {/* Orbe Rose Cœur Haut */}
                <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-[650px] h-[450px] bg-gradient-to-b from-rose-600/25 via-pink-500/15 to-transparent rounded-full blur-[120px] animate-love-halo"></div>
                {/* Orbe Violet Côté Gauche */}
                <div className="absolute top-1/3 -left-32 w-[500px] h-[500px] bg-gradient-to-r from-pink-600/20 via-purple-600/15 to-transparent rounded-full blur-[130px] animate-love-halo" style={{ animationDelay: '2s' }}></div>
                {/* Orbe Fuchsia Côté Droit */}
                <div className="absolute top-1/2 -right-32 w-[500px] h-[500px] bg-gradient-to-l from-rose-600/20 via-fuchsia-600/15 to-transparent rounded-full blur-[130px] animate-love-halo" style={{ animationDelay: '1s' }}></div>
                {/* Orbe Doux Bas */}
                <div className="absolute -bottom-24 left-1/3 w-[600px] h-[400px] bg-rose-900/20 rounded-full blur-[140px]"></div>

                {/* --- PARTICULES DE CŒURS VOLANTS FLOTTANTES --- */}
                {FLOATING_HEARTS.map((h, i) => (
                    <div
                        key={`heart-bg-${i}`}
                        className={`absolute select-none pointer-events-none ${h.size} animate-float-heart`}
                        style={{
                            left: h.left,
                            bottom: '2%',
                            animationDelay: h.delay,
                            animationDuration: h.duration,
                            filter: 'drop-shadow(0 0 10px rgba(244,63,94,0.6))',
                        }}
                    >
                        {h.type === 'heart' ? <HeartIcon className={`${h.size} text-pink-500/20 fill-pink-500/10`} /> : <SparklesIcon className={`${h.size} text-purple-400/20`} />}
                    </div>
                ))}
            </div>

            <div className="container mx-auto px-4 relative z-10 max-w-5xl">
                
                {!result ? (
                    /* --- PHASE SELECTION D'AMOUR --- */
                    <div className="animate-fade-in-up max-w-4xl mx-auto">
                        
                        {/* EN-TÊTE ROMANTIQUE */}
                        <div className="text-center mb-8 relative">
                            
                            {/* Petit Badge Romantique */}
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-rose-950/80 via-pink-900/60 to-purple-950/80 border border-pink-500/40 shadow-[0_0_20px_rgba(244,63,94,0.3)] mb-4">
                                <HeartIcon className="w-4 h-4 text-pink-400 fill-pink-400 animate-heartbeat inline" />
                                <span className="text-[11px] font-black uppercase tracking-widest text-pink-300">
                                    L'Oracle des Âmes Sœurs & de la Passion
                                </span>
                                <SparklesIcon className="w-4 h-4 text-amber-300 animate-heartbeat inline" />
                            </div>

                            {/* Titre Principal Pulsant d'Amour */}
                            <h2 className="text-4xl sm:text-5xl md:text-6xl font-serif font-black text-transparent bg-clip-text bg-gradient-to-r from-rose-300 via-pink-200 to-rose-400 mb-3 drop-shadow-[0_0_25px_rgba(244,63,94,0.5)] flex items-center justify-center gap-3">
                                <span>Astro Match</span>
                                <span className="inline-block animate-heartbeat text-rose-500 drop-shadow-[0_0_20px_rgba(244,63,94,0.8)]">
                                    
                                </span>
                            </h2>

                            <p className="text-purple-200/90 max-w-xl mx-auto text-sm sm:text-base font-light leading-relaxed">
                                Découvrez l'alchimie secrète, l'intensité passionnelle et la destinée amoureuse entre vos deux signes célestes.
                            </p>
                        </div>

                        {/* --- NEXUS ROMANTIQUE CENTRAL (RÉSUMÉ EN DIRECT) --- */}
                        <div className="mb-8 flex items-center justify-center">
                            <div className="bg-gradient-to-r from-rose-950/70 via-purple-950/80 to-pink-950/70 border border-rose-500/40 rounded-2xl px-6 py-3.5 shadow-[0_0_30px_rgba(244,63,94,0.25)] flex items-center gap-4 sm:gap-6 backdrop-blur-xl transition-all">
                                
                                {/* Signe 1 Capsule */}
                                <div className="flex items-center gap-2">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl transition-all duration-500 ${
                                        sign1 
                                        ? 'bg-gradient-to-br from-rose-500 to-pink-600 text-white shadow-[0_0_15px_rgba(244,63,94,0.8)] scale-110' 
                                        : 'bg-black/50 border border-dashed border-rose-500/40 text-gray-500'
                                    }`}>
                                        {sign1 ? <ZodiacIcon sign={sign1} className="w-6 h-6 text-white" /> : <UserIcon className="w-8 h-8 text-purple-300" />}
                                    </div>
                                    <div className="text-left">
                                        <span className="text-[10px] text-pink-400 uppercase font-black tracking-wider block">Vous</span>
                                        <span className="text-xs font-bold text-white">{sign1 || "Sélectionnez"}</span>
                                    </div>
                                </div>

                                {/* Cœur de Connexion Pulsant */}
                                <div className="flex flex-col items-center justify-center px-2">
                                    <span className={`text-2xl transition-all duration-300 ${sign1 && sign2 ? 'animate-heartbeat text-rose-500 scale-125' : 'text-gray-500'}`}>
                                        <HeartIcon className={`w-8 h-8 ${sign1 && sign2 ? 'text-pink-500 fill-pink-500 animate-pulse' : 'text-purple-400/40'}`} />
                                    </span>
                                    <span className="text-[9px] font-black uppercase tracking-widest text-pink-300/80 mt-0.5 whitespace-nowrap">
                                        {sign1 && sign2 ? "Alchimie Prête" : "En Attente"}
                                    </span>
                                </div>

                                {/* Signe 2 Capsule */}
                                <div className="flex items-center gap-2">
                                    <div className="text-right">
                                        <span className="text-[10px] text-purple-400 uppercase font-black tracking-wider block">Votre Autre</span>
                                        <span className="text-xs font-bold text-white">{sign2 || "Sélectionnez"}</span>
                                    </div>
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl transition-all duration-500 ${
                                        sign2 
                                        ? 'bg-gradient-to-br from-purple-500 to-indigo-600 text-white shadow-[0_0_15px_rgba(168,85,247,0.8)] scale-110' 
                                        : 'bg-black/50 border border-dashed border-purple-500/40 text-gray-500'
                                    }`}>
                                        {sign2 ? <ZodiacIcon sign={sign2} className="w-6 h-6 text-white" /> : <HeartIcon className="w-8 h-8 text-pink-400" />}
                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* --- LES DEUX CARTES DE SÉLECTION ENTOURÉES D'AMOUR --- */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-8">
                            
                            {/* CARTE SIGNE 1 : VOUS */}
                            <div className="bg-gradient-to-b from-[#180F2E]/95 via-[#120A24]/95 to-[#0D071A]/95 p-5 sm:p-6 rounded-3xl border-2 border-rose-500/40 hover:border-rose-400 shadow-[0_0_35px_rgba(244,63,94,0.2)] backdrop-blur-xl relative overflow-hidden transition-all duration-300">
                                
                                {/* Aura rose décorative interne */}
                                <div className="absolute top-0 right-0 w-40 h-40 bg-rose-500/10 rounded-full blur-[60px] pointer-events-none"></div>

                                <div className="flex items-center justify-between mb-4 border-b border-rose-500/20 pb-3">
                                    <h3 className="text-rose-300 font-extrabold uppercase tracking-wider text-xs flex items-center gap-2">
                                        <UserIcon className="w-4 h-4 text-rose-400" />
                                        <span>Votre Signe Astral</span>
                                    </h3>
                                    {sign1 && (
                                        <span className="text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/40 animate-pulse">
                                            {ZODIAC_DATA[sign1]?.element}
                                        </span>
                                    )}
                                </div>

                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                                    {Object.values(ZodiacSign).map((s) => {
                                        const isSelected = sign1 === s;
                                        return (
                                            <button
                                                key={`s1-${s}`}
                                                onClick={() => setSign1(s)}
                                                className={`p-2.5 sm:p-3 rounded-2xl flex flex-col items-center justify-center transition-all duration-200 cursor-pointer relative overflow-hidden group ${
                                                    isSelected 
                                                    ? 'bg-gradient-to-br from-rose-500 via-pink-500 to-rose-600 text-white font-black scale-105 shadow-[0_0_20px_rgba(244,63,94,0.7)] border-2 border-rose-200 ring-2 ring-rose-400/50' 
                                                    : 'bg-black/50 hover:bg-rose-950/40 text-gray-300 hover:text-white border border-rose-900/30 hover:border-rose-500/50 hover:shadow-[0_0_12px_rgba(244,63,94,0.25)] hover:-translate-y-0.5'
                                                }`}
                                            >
                                                <div className={`mb-1 transition-transform group-hover:scale-110 ${isSelected ? 'animate-heartbeat' : ''}`}><ZodiacIcon sign={s} className="w-6 h-6" /></div>
                                                <span className="text-[11px] font-bold truncate w-full text-center tracking-tight">
                                                    {s}
                                                </span>
                                                <span className="text-[9px] text-gray-400 group-hover:text-pink-300 opacity-70 truncate">
                                                    {ZODIAC_DATA[s]?.dates.split(' - ')[0]}
                                                </span>

                                                {isSelected && (
                                                    <span className="absolute top-1 right-1"><HeartIcon className="w-2.5 h-2.5 text-pink-400 fill-pink-400" /></span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* CARTE SIGNE 2 : VOTRE AUTRE */}
                            <div className="bg-gradient-to-b from-[#1E0F33]/95 via-[#140A26]/95 to-[#0D071A]/95 p-5 sm:p-6 rounded-3xl border-2 border-purple-500/40 hover:border-pink-400 shadow-[0_0_35px_rgba(236,72,153,0.2)] backdrop-blur-xl relative overflow-hidden transition-all duration-300">
                                
                                {/* Aura violette décorative interne */}
                                <div className="absolute top-0 right-0 w-40 h-40 bg-purple-500/10 rounded-full blur-[60px] pointer-events-none"></div>

                                <div className="flex items-center justify-between mb-4 border-b border-purple-500/20 pb-3">
                                    <h3 className="text-purple-300 font-extrabold uppercase tracking-wider text-xs flex items-center gap-2">
                                        <HeartIcon className="w-4 h-4 text-purple-400 fill-purple-400" />
                                        <span>Signe de Votre Âme Sœur</span>
                                    </h3>
                                    {sign2 && (
                                        <span className="text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/40 animate-pulse">
                                            {ZODIAC_DATA[sign2]?.element}
                                        </span>
                                    )}
                                </div>

                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                                    {Object.values(ZodiacSign).map((s) => {
                                        const isSelected = sign2 === s;
                                        return (
                                            <button
                                                key={`s2-${s}`}
                                                onClick={() => setSign2(s)}
                                                className={`p-2.5 sm:p-3 rounded-2xl flex flex-col items-center justify-center transition-all duration-200 cursor-pointer relative overflow-hidden group ${
                                                    isSelected 
                                                    ? 'bg-gradient-to-br from-purple-600 via-pink-600 to-rose-600 text-white font-black scale-105 shadow-[0_0_20px_rgba(168,85,247,0.7)] border-2 border-purple-200 ring-2 ring-purple-400/50' 
                                                    : 'bg-black/50 hover:bg-purple-950/40 text-gray-300 hover:text-white border border-purple-900/30 hover:border-purple-500/50 hover:shadow-[0_0_12px_rgba(168,85,247,0.25)] hover:-translate-y-0.5'
                                                }`}
                                            >
                                                <div className={`mb-1 transition-transform group-hover:scale-110 ${isSelected ? 'animate-heartbeat' : ''}`}><ZodiacIcon sign={s} className="w-6 h-6" /></div>
                                                <span className="text-[11px] font-bold truncate w-full text-center tracking-tight">
                                                    {s}
                                                </span>
                                                <span className="text-[9px] text-gray-400 group-hover:text-purple-300 opacity-70 truncate">
                                                    {ZODIAC_DATA[s]?.dates.split(' - ')[0]}
                                                </span>

                                                {isSelected && (
                                                    <span className="absolute top-1 right-1"><HeartIcon className="w-2.5 h-2.5 text-pink-400 fill-pink-400" /></span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                        </div>

                        {/* --- BOUTON D'ACTION MAGNÉTIQUE RAYONNANT D'AMOUR --- */}
                        <div className="text-center pt-2">
                            <button
                                onClick={handleCalculate}
                                disabled={!sign1 || !sign2 || loading}
                                className={`group relative px-10 sm:px-14 py-4 sm:py-5 rounded-full font-black uppercase tracking-widest text-xs sm:text-sm transition-all duration-300 overflow-hidden ${
                                    sign1 && sign2 && !loading
                                    ? 'bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 hover:from-rose-500 hover:via-pink-500 hover:to-purple-500 text-white shadow-[0_0_40px_rgba(244,63,94,0.7)] hover:shadow-[0_0_55px_rgba(244,63,94,0.9)] scale-105 cursor-pointer active:scale-95 border-2 border-rose-300'
                                    : 'bg-gray-900/80 text-gray-500 border border-gray-800 cursor-not-allowed opacity-60'
                                }`}
                            >
                                {/* Reflet de lumière animé qui balaie le bouton */}
                                {sign1 && sign2 && !loading && (
                                    <div className="absolute inset-0 w-1/3 h-full bg-white/20 skew-x-12 blur-sm animate-love-shine pointer-events-none"></div>
                                )}

                                {loading ? (
                                    <span className="flex items-center justify-center gap-3">
                                        <span className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin"></span>
                                        <span>Fusion des Énergies Amoureuses...</span>
                                        <HeartIcon className="w-4 h-4 text-pink-400 fill-pink-400 animate-heartbeat inline" />
                                    </span>
                                ) : (
                                    <span className="flex items-center justify-center gap-2.5">
                                        <HeartIcon className="w-4 h-4 text-pink-400 fill-pink-400 inline" />
                                        <span>Révéler Notre Compatibilité Amoureuse</span>
                                        <SparklesIcon className="w-4 h-4 text-amber-300 inline" />
                                    </span>
                                )}
                            </button>
                            
                            {!sign1 || !sign2 ? (
                                <p className="text-[11px] text-pink-300/70 mt-3 font-medium flex items-center justify-center gap-1.5">
                                    Sélectionnez les deux signes pour allumer le flambeau de la guidance
                                </p>
                            ) : null}
                        </div>

                    </div>
                ) : (
                    /* --- PHASE RÉSULTAT MAGIQUE IMMERGÉ D'AMOUR --- */
                    <div className="animate-fade-in max-w-4xl mx-auto space-y-6">
                        
                        {/* HEADER DU RÉSULTAT ENTOURÉ D'AMOUR */}
                        <div className="text-center bg-gradient-to-b from-[#220F38]/98 via-[#180A2B]/98 to-[#0E0720]/98 p-6 sm:p-10 rounded-3xl border-2 border-rose-500/50 shadow-[0_0_50px_rgba(244,63,94,0.35)] backdrop-blur-2xl relative overflow-hidden">
                            
                            {/* Orbe d'amour intérieur */}
                            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-80 h-80 bg-rose-500/20 rounded-full blur-[80px] pointer-events-none"></div>

                            {/* Ruban Tag d'Affinité Céleste */}
                            <div className="inline-flex items-center gap-2 px-5 py-1.5 rounded-full bg-gradient-to-r from-rose-500/30 via-pink-500/30 to-purple-500/30 border border-rose-400/50 text-rose-200 text-xs font-black uppercase tracking-wider mb-6 shadow-inner">
                                <HeartIcon className="w-4 h-4 text-pink-400 fill-pink-400 animate-heartbeat inline" />
                                <span>{loveTag}</span>
                                <SparklesIcon className="w-4 h-4 text-amber-300 animate-heartbeat inline" />
                            </div>
                            
                            {/* Duo des Signes Réunis */}
                            <div className="flex justify-center items-center gap-6 sm:gap-12 mb-6">
                                
                                {/* Signe 1 */}
                                <div className="text-center group">
                                    <div className="w-18 h-18 sm:w-22 sm:h-22 mx-auto rounded-3xl bg-gradient-to-br from-rose-500 to-pink-600 p-0.5 shadow-[0_0_25px_rgba(244,63,94,0.6)]">
                                        <div className="w-full h-full bg-[#120A24] rounded-3xl flex items-center justify-center">
                                            <span className="text-4xl sm:text-5xl">{ZODIAC_DATA[sign1 || '']?.symbol}</span>
                                        </div>
                                    </div>
                                    <span className="text-sm sm:text-base font-black text-rose-300 mt-2 block">{sign1}</span>
                                    <span className="text-[10px] text-gray-400 block">{ZODIAC_DATA[sign1 || '']?.element}</span>
                                </div>

                                {/* Cœur Central Battant */}
                                <div className="flex flex-col items-center">
                                    <span className="text-4xl sm:text-6xl animate-heartbeat text-rose-500 filter drop-shadow-[0_0_20px_rgba(244,63,94,0.9)]">
                                        
                                    </span>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-pink-300 mt-2">
                                        Alchimie
                                    </span>
                                </div>

                                {/* Signe 2 */}
                                <div className="text-center group">
                                    <div className="w-18 h-18 sm:w-22 sm:h-22 mx-auto rounded-3xl bg-gradient-to-br from-purple-500 to-indigo-600 p-0.5 shadow-[0_0_25px_rgba(168,85,247,0.6)]">
                                        <div className="w-full h-full bg-[#120A24] rounded-3xl flex items-center justify-center">
                                            <span className="text-4xl sm:text-5xl">{ZODIAC_DATA[sign2 || '']?.symbol}</span>
                                        </div>
                                    </div>
                                    <span className="text-sm sm:text-base font-black text-purple-300 mt-2 block">{sign2}</span>
                                    <span className="text-[10px] text-gray-400 block">{ZODIAC_DATA[sign2 || '']?.element}</span>
                                </div>

                            </div>

                            {/* SCORE GÉANT ENTOURÉ DE LUMIÈRE */}
                            <div className="my-6">
                                <div className="inline-block relative">
                                    <span className="text-6xl sm:text-7xl md:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-rose-400 via-pink-300 to-amber-300 tracking-tight drop-shadow-[0_0_35px_rgba(244,63,94,0.6)]">
                                        {displayScore}%
                                    </span>
                                </div>
                                <h3 className="text-xl sm:text-2xl md:text-3xl font-serif font-black text-white mt-2 drop-shadow-md">
                                    {result.title}
                                </h3>
                            </div>

                        </div>

                        {/* --- 3 SECTIONS DÉTAILLÉES ROMANTIQUES --- */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            
                            {/* 1. ÉMOTIONNEL */}
                            <div className="bg-gradient-to-b from-[#1C0F33]/90 to-[#120A24]/90 p-6 rounded-3xl border border-rose-500/40 shadow-xl space-y-2.5 backdrop-blur-md">
                                <h4 className="text-xs font-black uppercase tracking-wider text-rose-400 flex items-center gap-2">
                                    <HeartIcon className="w-4 h-4 text-pink-400 fill-pink-400 animate-heartbeat inline" />
                                    <span>Connexion Émotionnelle</span>
                                </h4>
                                <p className="text-xs sm:text-sm text-purple-100/90 leading-relaxed font-light">
                                    {result.emotional}
                                </p>
                            </div>

                            {/* 2. COMMUNICATION */}
                            <div className="bg-gradient-to-b from-[#1C0F33]/90 to-[#120A24]/90 p-6 rounded-3xl border border-pink-500/40 shadow-xl space-y-2.5 backdrop-blur-md">
                                <h4 className="text-xs font-black uppercase tracking-wider text-pink-400 flex items-center gap-2">
                                    <MessageCircleIcon className="w-4 h-4 text-purple-300 inline" />
                                    <span>Communication & Entente</span>
                                </h4>
                                <p className="text-xs sm:text-sm text-purple-100/90 leading-relaxed font-light">
                                    {result.communication}
                                </p>
                            </div>

                            {/* 3. INTIMITÉ & PASSION */}
                            <div className="bg-gradient-to-b from-[#1C0F33]/90 to-[#120A24]/90 p-6 rounded-3xl border border-amber-500/40 shadow-xl space-y-2.5 backdrop-blur-md">
                                <h4 className="text-xs font-black uppercase tracking-wider text-amber-400 flex items-center gap-2">
                                    <FlameIcon className="w-4 h-4 text-amber-400 inline" />
                                    <span>Intimité & Alchimie</span>
                                </h4>
                                <p className="text-xs sm:text-sm text-purple-100/90 leading-relaxed font-light">
                                    {result.intimacy}
                                </p>
                            </div>

                        </div>

                        {/* --- DÉFIS & CONSEIL DE L'ASTROLOGUE --- */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            
                            {/* Défis du Couple */}
                            {result.challenges && result.challenges.length > 0 && (
                                <div className="bg-gradient-to-b from-[#1A0E2B]/90 to-[#10071C]/90 p-6 rounded-3xl border border-amber-500/30 space-y-3">
                                    <h4 className="text-xs font-black uppercase tracking-wider text-amber-400 flex items-center gap-2">
                                        <AlertCircleIcon className="w-4 h-4 text-amber-400 inline" />
                                        <span>Points de Vigilance du Couple</span>
                                    </h4>
                                    <ul className="space-y-2 text-xs sm:text-sm text-purple-200/90">
                                        {result.challenges.map((ch, idx) => (
                                            <li key={idx} className="flex items-start gap-2.5">
                                                <span className="text-amber-400 font-bold mt-0.5">•</span>
                                                <span>{ch}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Le Conseil Mystique de l'Amour */}
                            <div className="bg-gradient-to-br from-rose-950/80 via-purple-950/80 to-[#120A24] p-6 rounded-3xl border-2 border-rose-500/50 shadow-xl flex flex-col justify-center text-center relative overflow-hidden">
                                
                                <h4 className="text-xs font-black uppercase tracking-wider text-rose-300 mb-2 flex items-center justify-center gap-1.5">
                                    Le Conseil de l'Oracle de l'Amour
                                </h4>
                                <p className="text-xs sm:text-sm text-white italic leading-relaxed font-serif">
                                    "{result.advice}"
                                </p>
                            </div>

                        </div>

                        {/* --- BOUTONS D'ACTION ROMANTIQUES --- */}
                        <div className="flex flex-col sm:flex-row justify-center items-center gap-3.5 pt-4">
                            <button
                                onClick={reset}
                                className="w-full sm:w-auto px-7 py-3.5 rounded-full bg-[#1A1033] hover:bg-rose-950/60 text-purple-200 hover:text-white text-xs font-extrabold border border-rose-500/30 transition-all cursor-pointer flex items-center justify-center gap-2 shadow-md hover:scale-105"
                            >
                                <RefreshCwIcon className="w-4 h-4 inline" />
                                <span>Tester un Autre Couple</span>
                            </button>
                            
                            <a
                                href="tel:0985405274"
                                className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 hover:from-rose-500 hover:to-purple-500 text-white text-xs font-black uppercase tracking-wider shadow-[0_0_25px_rgba(244,63,94,0.6)] flex items-center justify-center gap-2.5 transition-all hover:scale-105"
                            >
                                <PhoneIcon className="w-4 h-4 animate-bounce" />
                                <span>Consultation Amoureuse Directe au 09 85 40 52 74</span>
                            </a>
                        </div>

                    </div>
                )}

            </div>
        </section>
    );
};

export default LoveCompatibility;
